import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, BookOpen, Tag, ArrowRight, User } from "lucide-react";
import Navbar from "@/components/Navbar";
import { API } from "@/context/AuthContext";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = ["Leadership", "Strategy", "Technology", "Finance", "Marketing", "HR", "Entrepreneurship", "Operations"];

export default function KnowledgeHub() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [localQuery, setLocalQuery] = useState(searchParams.get("q") || "");
  const { user } = useAuth();

  const q = searchParams.get("q") || "";
  const category = searchParams.get("category") || "";
  const page = parseInt(searchParams.get("page") || "1");

  useEffect(() => { fetchArticles(); }, [searchParams]);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page });
      if (q) params.set("q", q);
      if (category) params.set("category", category);
      const res = await fetch(`${API}/knowledge/articles?${params}`);
      if (res.ok) { const data = await res.json(); setArticles(data.articles || []); setTotal(data.total || 0); }
    } finally { setLoading(false); }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const p = new URLSearchParams(searchParams);
    if (localQuery) p.set("q", localQuery); else p.delete("q");
    p.set("page", "1");
    setSearchParams(p);
  };

  const setFilter = (key, value) => {
    const p = new URLSearchParams(searchParams);
    if (value) p.set(key, value); else p.delete(key);
    p.set("page", "1");
    setSearchParams(p);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Navbar />
      <div className="pt-20 pb-16">
        <div className="bg-white border-b border-[#E4E4E1] py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <p className="section-label mb-3">Knowledge Hub</p>
            <h1 className="text-4xl font-normal text-[#0F172A] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Insights from the Council
            </h1>
            <p className="text-[#52525B] text-base max-w-xl mb-6">
              Articles, insights, and thought leadership from accomplished professionals across industries.
            </p>
            <form onSubmit={handleSearch} className="flex gap-0 max-w-xl">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
                <input type="text" placeholder="Search articles..." value={localQuery} onChange={(e) => setLocalQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-[#E4E4E1] border-r-0 bg-white text-sm focus:outline-none focus:border-[#0F172A] rounded-l-sm" data-testid="knowledge-search-input" />
              </div>
              <button type="submit" className="bg-[#0F172A] text-white px-6 py-3 text-sm font-semibold hover:bg-[#1E293B] transition-colors rounded-r-sm" data-testid="knowledge-search-btn">
                Search
              </button>
            </form>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Category filters */}
          <div className="flex flex-wrap gap-2 mb-8">
            <button onClick={() => setFilter("category", "")}
              className={`text-xs px-4 py-2 rounded-sm border transition-colors ${!category ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-[#52525B] border-[#E4E4E1] hover:border-[#0F172A]"}`}
              data-testid="filter-all-categories">All</button>
            {CATEGORIES.map((cat) => (
              <button key={cat} onClick={() => setFilter("category", category === cat ? "" : cat)}
                className={`text-xs px-4 py-2 rounded-sm border transition-colors ${category === cat ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-[#52525B] border-[#E4E4E1] hover:border-[#0F172A]"}`}
                data-testid={`filter-category-${cat.toLowerCase()}`}>
                {cat}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {[1,2,3,4,5,6].map(i => <div key={i} className="h-48 bg-white border border-[#E4E4E1] rounded-sm animate-pulse" />)}
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-20">
              <BookOpen size={40} className="text-[#A1A1AA] mx-auto mb-4" />
              <p className="text-[#52525B] text-sm">No articles found.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 stagger-children" data-testid="articles-grid">
              {articles.map((art) => (
                <article key={art.article_id} className="bg-white border border-[#E4E4E1] rounded-sm p-6 hover:border-[#0F172A] transition-colors cursor-pointer expert-card">
                  {art.category && (
                    <span className="section-label text-[#064E3B] mb-3 block">{art.category}</span>
                  )}
                  <h2 className="text-base font-semibold text-[#0F172A] mb-2 leading-snug line-clamp-2">{art.title}</h2>
                  <p className="text-sm text-[#52525B] leading-relaxed mb-4 line-clamp-3">{art.summary}</p>
                  <div className="flex flex-wrap gap-1 mb-4">
                    {(art.tags || []).slice(0, 3).map((tag) => (
                      <span key={tag} className="text-[10px] bg-[#F4F4F1] text-[#52525B] px-2 py-0.5 rounded-sm">
                        <Tag size={9} className="inline mr-1" />{tag}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-[#E4E4E1]">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-[#0F172A] flex items-center justify-center">
                        <User size={12} className="text-white" />
                      </div>
                      <span className="text-xs text-[#52525B]">{art.author?.name || "Council Member"}</span>
                    </div>
                    <span className="text-xs text-[#A1A1AA]">
                      {art.published_at ? new Date(art.published_at).toLocaleDateString("en-IN", { month: "short", day: "numeric", year: "numeric" }) : ""}
                    </span>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
